import React, { Fragment, useCallback, useEffect, useState } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import {
 Badge,
 Button,
 Card,
 Col,
 Form,
 Modal,
 ProgressBar,
 Row,
 Spinner,
 Table,
} from "react-bootstrap";
import { useSelector } from "react-redux";
import swal from "sweetalert";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";

/* 
 CONSTANTS
 */
const CATEGORIES = [
 "Design", "Procurement", "Construction", "Testing",
 "Handover", "Payment", "Regulatory", "Other",
];

const STATUSES = [
 "Not Started", "In Progress", "Completed",
 "Delayed", "On Hold", "Cancelled",
];

const DELAY_RESP_CODES = [
 { value: "", label: "—" },
 { value: "C", label: "C — Contractor" },
 { value: "PMC", label: "PMC — Client Instruction" },
 { value: "R", label: "R — Regulatory/Statutory" },
 { value: "FM", label: "FM — Force Majeure" },
 { value: "WC", label: "WC — Weather Conditions" },
];

/* Alert level → Bootstrap variant + row class */
const ALERT_META = {
 Green: { variant: "success", rowClass: "", label: "Green — On Track" },
 Amber: { variant: "warning", rowClass: "table-warning", label: "Amber — Recovery" },
 Red: { variant: "danger", rowClass: "table-danger", label: "Red — Escalation L2" },
 Critical: { variant: "dark", rowClass: "table-danger", label: "Critical — MD/CEO" },
};

const STATUS_VARIANT = {
 "Not Started": "secondary",
 "In Progress": "primary",
 "Completed": "success",
 "Delayed": "danger",
 "On Hold": "warning",
 "Cancelled": "dark",
};

/* 
 HELPERS
 */
const fmtDate = (v) =>
 v ? new Date(v).toLocaleDateString("en-IN") : "—";

const toInputDate = (v) => {
 if (!v) return "";
 const d = new Date(v);
 return isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
};

const emptyForm = () => ({
 milestoneName: "",
 description: "",
 category: "Construction",
 plannedStartDate: "",
 plannedEndDate: "",
 revisedEndDate: "",
 forecastEndDate: "",
 actualStartDate: "",
 actualEndDate: "",
 status: "Not Started",
 completionPercentage: 0,
 responsibleUser: "",
 delayResponsibility: "",
 isForceMajeure: false,
 hasConcurrentDelay: false,
 remarks: "",
 workOrder: "",
});

/* 
 SUMMARY CARD
 */
const SummaryCard = ({ label, value, color, border }) => (
 <Col xs={6} sm={4} xl={2}>
 <Card className="border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${border}`, borderRadius: 8 }}>
 <Card.Body className="py-3 px-3">
 <div className="text-muted small mb-1">{label}</div>
 <h4 className="fw-bold mb-0" style={{ color }}>{value}</h4>
 </Card.Body>
 </Card>
 </Col>
);

/* 
 MAIN COMPONENT
 */
const MilestoneTracker = () => {
 const user = useSelector((s) => s.auth?.userDetails || {});
 const isAdmin = (user.role?.name || user.roleName || "").toLowerCase() === "admin";
 const canManage =
 isAdmin ||
 (user.permissionNames || []).some((p) =>
 ["create:projects", "edit:projects", "create:workorders", "edit:workorders"].includes(p.toLowerCase())
 );

 /* State */
 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [users, setUsers] = useState([]);

 const [selectedProject, setSelectedProject] = usePersistentState("milestone_project", "");
 const [selectedWO, setSelectedWO] = usePersistentState("milestone_wo", "");
 const [filterStatus, setFilterStatus] = usePersistentState("milestone_status", "");
 const [filterAlert, setFilterAlert] = usePersistentState("milestone_alert", "");
 const [filterCategory, setFilterCategory] = usePersistentState("milestone_cat", "");
 const [searchText, setSearchText] = usePersistentState("milestone_search", "");

 const [milestones, setMilestones] = useState([]);
 const [summary, setSummary] = useState(null);
 const [alerts, setAlerts] = useState([]);

 const [loading, setLoading] = useState(false);
 const [alertsLoading, setAlertsLoading] = useState(false);
 const [saving, setSaving] = useState(false);

 const [activeTab, setActiveTab] = useState("register");

 const [showModal, setShowModal] = useState(false);
 const [editingId, setEditingId] = useState(null);
 const [form, setForm] = useState(emptyForm());

 const [importingWO, setImportingWO] = useState("");
 const [importing, setImporting] = useState(false);

 /* Loaders */
 const loadProjects = useCallback(async () => {
 try {
 const res = await api.get("/projects?limit=500");
 if (res.data?.success) setProjects(res.data.data || []);
 } catch { /* silent */ }
 }, []);

 const loadWorkOrders = useCallback(async () => {
 if (!selectedProject) { setWorkOrders([]); return; }
 try {
 const res = await api.get(`/projects/${selectedProject}/work-orders`);
 if (res.data?.success) setWorkOrders(res.data.data || []);
 } catch { setWorkOrders([]); }
 }, [selectedProject]);

 const loadUsers = useCallback(async () => {
 try {
 const res = await api.get("/users?limit=200");
 if (res.data?.success) setUsers(res.data.data || []);
 } catch { /* silent */ }
 }, []);

 const loadMilestones = useCallback(async () => {
 if (!selectedProject) { setMilestones([]); setSummary(null); return; }
 setLoading(true);
 try {
 const params = { project: selectedProject };
 if (selectedWO) params.workOrder = selectedWO;
 if (filterStatus) params.status = filterStatus;
 if (filterAlert) params.alertLevel = filterAlert;
 if (filterCategory) params.category = filterCategory;
 if (searchText) params.search = searchText;

 const [listRes, registerRes] = await Promise.all([
 api.get("/milestones", { params }),
 api.get(`/milestones/register/${selectedProject}`),
 ]);

 setMilestones(listRes.data?.data || []);
 setSummary(registerRes.data?.data?.summary || null);
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to load milestones", "error");
 } finally {
 setLoading(false);
 }
 }, [selectedProject, selectedWO, filterStatus, filterAlert, filterCategory, searchText]);

 const loadAlerts = useCallback(async () => {
 setAlertsLoading(true);
 try {
 const params = {};
 if (selectedProject) params.project = selectedProject;
 const res = await api.get("/milestones/alerts", { params });
 setAlerts(res.data?.data || []);
 } catch { setAlerts([]); }
 finally { setAlertsLoading(false); }
 }, [selectedProject]);

 useEffect(() => { loadProjects(); loadUsers(); }, [loadProjects, loadUsers]);
 useEffect(() => { loadWorkOrders(); }, [loadWorkOrders]);
 useEffect(() => { loadMilestones(); }, [loadMilestones]);
 useEffect(() => { if (activeTab === "alerts") loadAlerts(); }, [activeTab, loadAlerts]);

 /* Modal helpers */
 const openCreate = () => {
 setEditingId(null);
 setForm({ ...emptyForm(), workOrder: selectedWO || "" });
 setShowModal(true);
 };

 const openEdit = (m) => {
 setEditingId(m._id);
 setForm({
 milestoneName: m.milestoneName || "",
 description: m.description || "",
 category: m.category || "Construction",
 plannedStartDate: toInputDate(m.plannedStartDate),
 plannedEndDate: toInputDate(m.plannedEndDate),
 revisedEndDate: toInputDate(m.revisedEndDate),
 forecastEndDate: toInputDate(m.forecastEndDate),
 actualStartDate: toInputDate(m.actualStartDate),
 actualEndDate: toInputDate(m.actualEndDate),
 status: m.status || "Not Started",
 completionPercentage: m.completionPercentage ?? 0,
 responsibleUser: m.responsibleUser?._id || m.responsibleUser || "",
 delayResponsibility: m.delayResponsibility || "",
 isForceMajeure: m.isForceMajeure || false,
 hasConcurrentDelay: m.hasConcurrentDelay || false,
 remarks: m.remarks || "",
 workOrder: m.workOrder?._id || m.workOrder || "",
 });
 setShowModal(true);
 };

 const setF = (key, val) => setForm((p) => ({ ...p, [key]: val }));

 const handleSave = async () => {
 if (!form.milestoneName.trim()) {
 swal("Validation", "Milestone name is required", "warning"); return;
 }
 if (!form.plannedEndDate) {
 swal("Validation", "Planned end date is required", "warning"); return;
 }
 setSaving(true);
 try {
 const payload = {
 ...form,
 project: selectedProject,
 workOrder: form.workOrder || null,
 responsibleUser: form.responsibleUser || null,
 completionPercentage: Number(form.completionPercentage) || 0,
 revisedEndDate: form.revisedEndDate || null,
 forecastEndDate: form.forecastEndDate || null,
 actualStartDate: form.actualStartDate || null,
 actualEndDate: form.actualEndDate || null,
 plannedStartDate: form.plannedStartDate || null,
 };

 if (editingId) {
 await api.patch(`/milestones/${editingId}`, payload);
 swal("Saved", "Milestone updated", "success");
 } else {
 await api.post("/milestones", payload);
 swal("Created", "Milestone created", "success");
 }
 setShowModal(false);
 loadMilestones();
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to save", "error");
 } finally {
 setSaving(false);
 }
 };

 const handleDelete = async (id) => {
 const ok = await swal({ title: "Delete this milestone?", icon: "warning", buttons: true, dangerMode: true });
 if (!ok) return;
 try {
 await api.delete(`/milestones/${id}`);
 swal("Deleted", "Milestone removed", "success");
 loadMilestones();
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to delete", "error");
 }
 };

 const handleImport = async () => {
 if (!importingWO) { swal("Warning", "Select a Work Order", "warning"); return; }
 setImporting(true);
 try {
 const res = await api.post(`/milestones/import-from-gantt/${importingWO}`);
 swal("Imported", res.data?.message || "Done", "success");
 loadMilestones();
 } catch (err) {
 swal("Error", err.response?.data?.message || "Import failed", "error");
 } finally { setImporting(false); setImportingWO(""); }
 };

 const alertCount = alerts.filter((a) => ["Amber", "Red", "Critical"].includes(a.alertLevel)).length;

 /* RENDER */
 return (
 <>
 <PageTitle activeMenu="Milestone Tracker" motherMenu="Projects" />

 {/* Header */}
 <Card className="mb-3 shadow-sm border-0">
 <Card.Body className="d-flex justify-content-between align-items-start flex-wrap gap-3">
 <div>
 <h4 className="fw-bold mb-1">
 <i className="fas fa-flag-checkered text-primary me-2" />
 Milestone Tracking &amp; Delay Alerts
 </h4>
 <p className="text-muted mb-0 small">
 Baseline vs Actual/Forecast — 4-level alert system per Delay Calculation Methodology.
 </p>
 </div>
 <div className="d-flex flex-wrap gap-2 align-items-end">
 <Form.Select
 size="sm" style={{ width: 220, height: 36 }}
 value={selectedProject}
 onChange={(e) => { setSelectedProject(e.target.value); setSelectedWO(""); }}
 >
 <option value="">— Select Project —</option>
 {projects.map((p) => (
 <option key={p._id} value={p._id}>{p.projectCode} — {p.projectName}</option>
 ))}
 </Form.Select>
 {selectedProject && workOrders.length > 0 && (
 <Form.Select
 size="sm" style={{ width: 180, height: 36 }}
 value={selectedWO}
 onChange={(e) => setSelectedWO(e.target.value)}
 >
 <option value="">All WOs</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>{wo.woNo}{wo.label ? ` – ${wo.label}` : ""}</option>
 ))}
 </Form.Select>
 )}
 {canManage && selectedProject && (
 <Button size="sm" variant="primary" style={{ height: 36 }} onClick={openCreate}>
 <i className="fas fa-plus me-1" /> Add Milestone
 </Button>
 )}
 {selectedProject && (
 <Button
 size="sm"
 variant="outline-success"
 style={{ height: 36 }}
 onClick={async () => {
 try {
 const params = new URLSearchParams({ project: selectedProject });
 if (selectedWO) params.append("workOrder", selectedWO);
 const res = await api.get(`/milestones/excel?${params.toString()}`, { responseType: "blob" });
 const url = window.URL.createObjectURL(res.data);
 const a = document.createElement("a");
 a.href = url;
 a.download = `Milestone_Register.xlsx`;
 document.body.appendChild(a);
 a.click();
 a.remove();
 window.URL.revokeObjectURL(url);
 } catch {
 swal("Error", "Failed to download Excel", "error");
 }
 }}
 >
 <i className="fas fa-file-excel me-1" /> Export Excel
 </Button>
 )}
 </div>
 </Card.Body>
 </Card>

 {/* Summary Cards */}
 {summary && (
 <Row className="g-2 mb-3">
 <SummaryCard label="Total" value={summary.total || 0} color="#6c757d" border="#6c757d" />
 <SummaryCard label="On Track" value={summary.onTrack || 0} color="#28a745" border="#28a745" />
 <SummaryCard label="Amber" value={summary.amber || 0} color="#856404" border="#ffc107" />
 <SummaryCard label="Red" value={summary.red || 0} color="#dc3545" border="#dc3545" />
 <SummaryCard label="Critical" value={summary.critical || 0} color="#111" border="#343a40" />
 <SummaryCard label="Avg Delay (days)" value={summary.avgDelay || 0} color="#0d6efd" border="#0d6efd" />
 </Row>
 )}

 {/* Alert Banner */}
 {activeTab === "register" && summary && (summary.critical > 0 || summary.red > 0) && (
 <div className="alert alert-danger d-flex align-items-center justify-content-between py-2 mb-3">
 <span>
 <i className="fas fa-exclamation-triangle me-2" />
 {summary.critical > 0 && <><strong>{summary.critical}</strong> Critical (MD/CEO escalation) · </>}
 {summary.red > 0 && <><strong>{summary.red}</strong> Red (L2 Escalation)</>}
 </span>
 <Button variant="danger" size="sm" onClick={() => setActiveTab("alerts")}>
 View Alerts →
 </Button>
 </div>
 )}

 {/* Tabs */}
 <div className="d-flex gap-2 mb-3 border-bottom pb-2 flex-wrap">
 <button
 className={`btn btn-sm ${activeTab === "register" ? "btn-primary" : "btn-outline-secondary"}`}
 onClick={() => setActiveTab("register")}
 >
 <i className="fas fa-list me-1" /> Milestone Register
 </button>
 <button
 className={`btn btn-sm ${activeTab === "alerts" ? "btn-danger" : "btn-outline-secondary"}`}
 onClick={() => setActiveTab("alerts")}
 >
 <i className="fas fa-bell me-1" /> Delay Alerts
 {alertCount > 0 && <Badge bg="warning" text="dark" className="ms-2">{alertCount}</Badge>}
 </button>
 {canManage && selectedProject && workOrders.length > 0 && (
 <div className="ms-auto d-flex gap-2 align-items-center">
 <Form.Select
 size="sm" style={{ width: 200 }}
 value={importingWO}
 onChange={(e) => setImportingWO(e.target.value)}
 >
 <option value="">Import from Gantt Chart…</option>
 {workOrders.map((wo) => <option key={wo._id} value={wo._id}>{wo.woNo}{wo.label ? ` – ${wo.label}` : ""}</option>)}
 </Form.Select>
 <Button size="sm" variant="outline-primary" onClick={handleImport} disabled={!importingWO || importing}>
 {importing ? <Spinner size="sm" /> : <i className="fas fa-file-import me-1" />}Import
 </Button>
 </div>
 )}
 </div>

 {/* TAB: REGISTER */}
 {activeTab === "register" && (
 <>
 {/* Filters */}
 <Row className="g-2 mb-3">
 <Col md={3}>
 <Form.Control size="sm" placeholder="Search milestones…" value={searchText}
 onChange={(e) => setSearchText(e.target.value)} />
 </Col>
 <Col md={2}>
 <Form.Select size="sm" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
 <option value="">All Categories</option>
 {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
 </Form.Select>
 </Col>
 <Col md={2}>
 <Form.Select size="sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
 <option value="">All Statuses</option>
 {STATUSES.map((s) => <option key={s}>{s}</option>)}
 </Form.Select>
 </Col>
 <Col md={2}>
 <Form.Select size="sm" value={filterAlert} onChange={(e) => setFilterAlert(e.target.value)}>
 <option value="">All Alert Levels</option>
 {["Green", "Amber", "Red", "Critical"].map((a) => <option key={a}>{a}</option>)}
 </Form.Select>
 </Col>
 <Col md={1}>
 <Button size="sm" variant="outline-secondary"
 onClick={() => { setFilterStatus(""); setFilterAlert(""); setFilterCategory(""); setSearchText(""); }}>
 Clear
 </Button>
 </Col>
 </Row>

 <Card className="shadow-sm border-0">
 <Card.Body className="p-0">
 {!selectedProject ? (
 <div className="text-center py-5 text-muted">
 <i className="fas fa-flag fs-2 mb-3 d-block text-primary opacity-50" />
 <h6>Select a Project to view milestone register</h6>
 </div>
 ) : loading ? (
 <div className="text-center py-5">
 <Spinner size="sm" animation="border" variant="primary" />
 </div>
 ) : milestones.length === 0 ? (
 <div className="text-center py-5 text-muted">
 <i className="fas fa-flag-checkered fs-2 mb-3 d-block opacity-50" />
 <h6>No milestones yet</h6>
 {canManage && (
 <Button size="sm" variant="primary" className="mt-2" onClick={openCreate}>
 <i className="fas fa-plus me-1" /> Add First Milestone
 </Button>
 )}
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover className="mb-0 align-middle" style={{ fontSize: 12 }}>
 <thead className="table-light">
 <tr>
 <th className="ps-3">Code</th>
 <th>Milestone</th>
 <th>Category</th>
 <th>WO</th>
 <th>Baseline End</th>
 <th>Revised End</th>
 <th>Forecast End</th>
 <th>Actual End</th>
 <th>%</th>
 <th>Status</th>
 <th>Alert Level</th>
 <th>Delay</th>
 <th>Resp.</th>
 <th>Cause</th>
 {canManage && <th className="text-center">Actions</th>}
 </tr>
 </thead>
 <tbody>
 {milestones.map((m) => {
 const meta = ALERT_META[m.alertLevel] || ALERT_META.Green;
 return (
 <Fragment key={m._id}>
 <tr className={meta.rowClass}>
 <td className="ps-3">
 <code className="text-primary" style={{ fontSize: 10 }}>{m.milestoneCode}</code>
 </td>
 <td>
 <div className="fw-semibold d-flex align-items-center gap-1">
 {m.milestoneName}
 {Array.isArray(m.subtasks) && m.subtasks.length > 0 && (
 <Badge bg="secondary" pill style={{ fontSize: 9, fontWeight: 500 }}>
 {m.subtasks.length} subs
 </Badge>
 )}
 </div>
 {m.description && (
 <div className="text-muted" style={{ fontSize: 10 }}>
 {m.description.length > 50 ? m.description.slice(0, 50) + "…" : m.description}
 </div>
 )}
 </td>
 <td>
 <Badge bg="light" text="dark" className="border" style={{ fontSize: 10 }}>{m.category}</Badge>
 </td>
 <td className="text-muted">{m.workOrder?.woNo || m.workOrder?.woNumber || "—"}</td>
 <td>{fmtDate(m.plannedEndDate)}</td>
 <td className={m.revisedEndDate ? "text-warning fw-semibold" : "text-muted"}>
 {fmtDate(m.revisedEndDate)}
 </td>
 <td className={m.forecastEndDate ? "text-primary" : "text-muted"}>
 {fmtDate(m.forecastEndDate)}
 </td>
 <td className={m.actualEndDate ? "text-success fw-semibold" : "text-muted"}>
 {fmtDate(m.actualEndDate)}
 </td>
 <td>
 <div className="d-flex align-items-center gap-1">
 <ProgressBar
 now={m.completionPercentage || 0}
 style={{ width: 45, height: 5 }}
 variant={
 m.alertLevel === "Green" ? "success" :
 m.alertLevel === "Amber" ? "warning" : "danger"
 }
 />
 <span style={{ fontSize: 10 }}>{m.completionPercentage || 0}%</span>
 </div>
 </td>
 <td>
 <Badge bg={STATUS_VARIANT[m.status] || "secondary"} style={{ fontSize: 10 }}>
 {m.status}
 </Badge>
 </td>
 <td>
 <Badge bg={meta.variant} style={{ fontSize: 10 }}>
 {m.alertLevel}
 </Badge>
 </td>
 <td className="text-center">
 {m.delayDays > 0 ? (
 <span className="fw-bold text-danger">{m.delayDays}d</span>
 ) : m.delayDays < 0 ? (
 <span className="fw-bold text-success">{Math.abs(m.delayDays)}d early</span>
 ) : "—"}
 </td>
 <td className="text-center">
 {m.delayResponsibility ? (
 <Badge bg="secondary" style={{ fontSize: 10 }}>{m.delayResponsibility}</Badge>
 ) : "—"}
 </td>
 <td className="text-center" style={{ fontSize: 11 }}>
 {m.isForceMajeure && <Badge bg="info" className="me-1" style={{ fontSize: 9 }}>FM</Badge>}
 {m.hasConcurrentDelay && <Badge bg="secondary" style={{ fontSize: 9 }}>CD</Badge>}
 </td>
 {canManage && (
 <td className="text-center">
 <Button variant="outline-primary" size="sm" className="me-1 px-2" onClick={() => openEdit(m)}>
 <i className="fas fa-edit" />
 </Button>
 <Button variant="outline-danger" size="sm" className="px-2" onClick={() => handleDelete(m._id)}>
 <i className="fas fa-trash" />
 </Button>
 </td>
 )}
 </tr>

 {/* Subpoints (Hierarchy Indentation) */}
 {Array.isArray(m.subtasks) && m.subtasks.map((sub, sIdx) => (
 <tr
 key={`${m._id}-sub-${sIdx}`}
 style={{
 background: "#fafbfc",
 fontSize: "0.83rem",
 borderBottom: "1px dashed #e9ecef",
 }}
 >
 <td className="ps-4 text-muted">
 <span
 className="badge bg-light text-dark border px-2 py-1"
 style={{ fontSize: 10, fontFamily: "monospace" }}
 >
 {sub.code || `${m.milestoneName?.split(".")[0] || ""}.${sIdx + 1}`}
 </span>
 </td>
 <td style={{ paddingLeft: "1.5rem" }}>
 <div className="d-flex align-items-baseline gap-2">
 <span className="text-muted" style={{ fontSize: 12 }}>↳</span>
 <div>
 <span className="text-dark fw-normal">{sub.task}</span>
 {sub.days && (
 <span className="text-muted ms-2" style={{ fontSize: 10 }}>
 ({sub.days} {sub.days === 1 ? "day" : "days"})
 </span>
 )}
 {sub.remarks && (
 <div className="text-muted" style={{ fontSize: 10 }}>
 {sub.remarks}
 </div>
 )}
 </div>
 </div>
 </td>
 <td>
 <span className="text-muted" style={{ fontSize: 10 }}>{m.category}</span>
 </td>
 <td className="text-muted" style={{ fontSize: 11 }}>{m.workOrder?.woNo || "—"}</td>
 <td className="text-muted" style={{ fontSize: 11 }}>
 {sub.endDate ? fmtDate(sub.endDate) : "—"}
 </td>
 <td className="text-muted text-center">—</td>
 <td className="text-muted text-center">—</td>
 <td className="text-muted text-center">—</td>
 <td>
 <div className="d-flex align-items-center gap-1">
 <ProgressBar
 now={sub.completionPercentage || 0}
 style={{ width: 40, height: 4 }}
 variant={sub.status === "Completed" ? "success" : "info"}
 />
 <span style={{ fontSize: 9 }}>{sub.completionPercentage || 0}%</span>
 </div>
 </td>
 <td>
 <Badge
 bg={STATUS_VARIANT[sub.status] || "light"}
 text={STATUS_VARIANT[sub.status] ? "white" : "dark"}
 className={STATUS_VARIANT[sub.status] ? "" : "border"}
 style={{ fontSize: 9 }}
 >
 {sub.status || "Not Started"}
 </Badge>
 </td>
 <td className="text-center text-muted" style={{ fontSize: 10 }}>—</td>
 <td className="text-center text-muted" style={{ fontSize: 10 }}>—</td>
 <td className="text-center text-muted" style={{ fontSize: 10 }}>—</td>
 <td className="text-center text-muted" style={{ fontSize: 10 }}>—</td>
 {canManage && <td className="text-center text-muted" style={{ fontSize: 10 }}>—</td>}
 </tr>
 ))}
 </Fragment>
 );
 })}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 </Card>

 {/* Critical panel */}
 {summary?.criticalMilestones?.length > 0 && (
 <Card className="mt-3 shadow-sm border-0">
 <Card.Header className="fw-semibold bg-danger text-white py-2">
 <i className="fas fa-exclamation-circle me-2" />
 Critical Milestones — Top 5 by Severity &amp; Delay
 </Card.Header>
 <Card.Body className="p-0">
 <Table size="sm" className="mb-0 align-middle" style={{ fontSize: 12 }}>
 <thead className="table-light">
 <tr>
 <th className="ps-3">Code</th>
 <th>Milestone</th>
 <th>Baseline End</th>
 <th>Delay</th>
 <th>Alert Level</th>
 <th>Cause Code</th>
 <th>Responsible</th>
 </tr>
 </thead>
 <tbody>
 {summary.criticalMilestones.map((m) => (
 <tr key={m._id} className={ALERT_META[m.alertLevel]?.rowClass || ""}>
 <td className="ps-3"><code>{m.milestoneCode}</code></td>
 <td className="fw-semibold">{m.milestoneName}</td>
 <td>{fmtDate(m.plannedEndDate)}</td>
 <td>
 <span className="fw-bold text-danger">
 {m.delayDays > 0 ? `${m.delayDays}d overdue` : "Imminent"}
 </span>
 </td>
 <td>
 <Badge bg={ALERT_META[m.alertLevel]?.variant || "secondary"} style={{ fontSize: 10 }}>
 {m.alertLevel}
 </Badge>
 </td>
 <td>
 {m.delayResponsibility
 ? <Badge bg="secondary" style={{ fontSize: 10 }}>{m.delayResponsibility}</Badge>
 : "—"}
 </td>
 <td className="text-muted">{m.responsibleUser?.name || "—"}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 </Card.Body>
 </Card>
 )}
 </>
 )}

 {/* TAB: DELAY ALERTS */}
 {activeTab === "alerts" && (
 <Card className="shadow-sm border-0">
 <Card.Header className="d-flex justify-content-between align-items-center py-3">
 <span className="fw-semibold">
 <i className="fas fa-bell text-danger me-2" />
 Delay Alerts — Amber / Red / Critical
 </span>
 <Button size="sm" variant="outline-secondary" onClick={loadAlerts} disabled={alertsLoading}>
 {alertsLoading ? <Spinner size="sm" /> : <i className="fas fa-sync me-1" />}Refresh
 </Button>
 </Card.Header>
 <Card.Body className="p-0">
 {alertsLoading ? (
 <div className="text-center py-5"><Spinner size="sm" animation="border" variant="danger" /></div>
 ) : alerts.length === 0 ? (
 <div className="text-center py-5 text-muted">
 <i className="fas fa-check-circle fs-2 mb-3 d-block text-success opacity-75" />
 <h6>No active delay alerts — all milestones on track</h6>
 </div>
 ) : (
 <>
 {/* Alert level legend */}
 <div className="px-3 py-2 bg-light border-bottom d-flex flex-wrap gap-3" style={{ fontSize: 11 }}>
 <span><Badge bg="warning" text="dark">Amber</Badge> 1–14 days — Recovery Programme required</span>
 <span><Badge bg="danger">Red</Badge> 15–30 days — L2 Escalation, Show Cause Notice</span>
 <span><Badge bg="dark">Critical</Badge> &gt;30 days — MD/CEO Escalation, LD applicable</span>
 </div>
 <div className="table-responsive">
 <Table hover className="mb-0 align-middle" style={{ fontSize: 12 }}>
 <thead className="table-light">
 <tr>
 <th className="ps-3">Level</th>
 <th>Code</th>
 <th>Milestone</th>
 <th>Project</th>
 <th>WO</th>
 <th>Baseline End</th>
 <th>Forecast End</th>
 <th>Delay (days)</th>
 <th>%</th>
 <th>Cause</th>
 <th>Flags</th>
 <th>Responsible</th>
 <th>Remarks</th>
 </tr>
 </thead>
 <tbody>
 {alerts.map((a) => {
 const meta = ALERT_META[a.alertLevel] || ALERT_META.Amber;
 return (
 <tr key={a._id} className={meta.rowClass}>
 <td className="ps-3">
 <Badge bg={meta.variant} style={{ fontSize: 10 }}>
 {a.alertLevel}
 </Badge>
 </td>
 <td><code style={{ fontSize: 10 }}>{a.milestoneCode}</code></td>
 <td className="fw-semibold">{a.milestoneName}</td>
 <td className="text-muted small">
 {a.project?.projectName || a.project?.projectCode || "—"}
 </td>
 <td className="text-muted small">{a.workOrder?.woNo || a.workOrder?.woNumber || "—"}</td>
 <td className="fw-semibold text-danger">
 {fmtDate(a.effectiveBaseline)}
 {a.revisedEndDate && (
 <div className="text-muted" style={{ fontSize: 10 }}>
 Orig: {fmtDate(a.plannedEndDate)}
 </div>
 )}
 </td>
 <td className="text-primary">{fmtDate(a.forecastEndDate)}</td>
 <td className="text-center fw-bold text-danger">
 {a.delayDays > 0 ? `${a.delayDays}d` : a.delayDays < 0 ? `${Math.abs(a.delayDays)}d early` : "—"}
 </td>
 <td>
 <div className="d-flex align-items-center gap-1">
 <ProgressBar
 now={a.completionPercentage || 0}
 style={{ width: 40, height: 5 }}
 variant={meta.variant}
 />
 <span style={{ fontSize: 10 }}>{a.completionPercentage || 0}%</span>
 </div>
 </td>
 <td>
 {a.delayResponsibility
 ? <Badge bg="secondary" style={{ fontSize: 10 }}>{a.delayResponsibility}</Badge>
 : "—"}
 </td>
 <td style={{ fontSize: 10 }}>
 {a.isForceMajeure && <Badge bg="info" className="me-1" style={{ fontSize: 9 }}>FM</Badge>}
 {a.hasConcurrentDelay && <Badge bg="secondary" style={{ fontSize: 9 }}>CD</Badge>}
 </td>
 <td className="text-muted small">
 {a.responsibleUser?.name || "—"}
 {a.responsibleUser?.email && (
 <div style={{ fontSize: 10 }}>{a.responsibleUser.email}</div>
 )}
 </td>
 <td className="text-muted small">{a.remarks || "—"}</td>
 </tr>
 );
 })}
 </tbody>
 </Table>
 </div>
 </>
 )}
 </Card.Body>
 </Card>
 )}

 {/* ADD / EDIT MODAL */}
 <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
 <Modal.Header closeButton>
 <Modal.Title style={{ fontSize: 15 }}>
 <i className={`fas fa-${editingId ? "edit" : "flag"} me-2 text-primary`} />
 {editingId ? "Edit Milestone" : "Add Milestone"}
 </Modal.Title>
 </Modal.Header>
 <Modal.Body>
 <Row className="g-3">
 <Col md={8}>
 <Form.Label className="small fw-semibold">
 Milestone Name <span className="text-danger">*</span>
 </Form.Label>
 <Form.Control size="sm" value={form.milestoneName}
 onChange={(e) => setF("milestoneName", e.target.value)} />
 </Col>
 <Col md={4}>
 <Form.Label className="small fw-semibold">Category</Form.Label>
 <Form.Select size="sm" value={form.category} onChange={(e) => setF("category", e.target.value)}>
 {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
 </Form.Select>
 </Col>
 <Col md={12}>
 <Form.Label className="small fw-semibold">Description</Form.Label>
 <Form.Control as="textarea" rows={2} size="sm" value={form.description}
 onChange={(e) => setF("description", e.target.value)} />
 </Col>
 <Col md={6}>
 <Form.Label className="small fw-semibold">Work Order</Form.Label>
 <Form.Select size="sm" value={form.workOrder} onChange={(e) => setF("workOrder", e.target.value)}>
 <option value="">Not linked</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>{wo.woNo}</option>
 ))}
 </Form.Select>
 </Col>
 <Col md={6}>
 <Form.Label className="small fw-semibold">Responsible Person</Form.Label>
 <Form.Select size="sm" value={form.responsibleUser} onChange={(e) => setF("responsibleUser", e.target.value)}>
 <option value="">— Not assigned —</option>
 {users.map((u) => <option key={u._id} value={u._id}>{u.name}</option>)}
 </Form.Select>
 </Col>

 {/* Dates */}
 <Col md={3}>
 <Form.Label className="small fw-semibold">Planned Start</Form.Label>
 <Form.Control type="date" size="sm" value={form.plannedStartDate}
 onChange={(e) => setF("plannedStartDate", e.target.value)} />
 </Col>
 <Col md={3}>
 <Form.Label className="small fw-semibold">
 Baseline End <span className="text-danger">*</span>
 </Form.Label>
 <Form.Control type="date" size="sm" value={form.plannedEndDate}
 onChange={(e) => setF("plannedEndDate", e.target.value)} />
 <Form.Text className="text-muted" style={{ fontSize: 10 }}>Locked baseline — delay calculated against this</Form.Text>
 </Col>
 <Col md={3}>
 <Form.Label className="small fw-semibold">Revised End</Form.Label>
 <Form.Control type="date" size="sm" value={form.revisedEndDate}
 onChange={(e) => setF("revisedEndDate", e.target.value)} />
 <Form.Text className="text-muted" style={{ fontSize: 10 }}>PMC-approved extension</Form.Text>
 </Col>
 <Col md={3}>
 <Form.Label className="small fw-semibold">Forecast End</Form.Label>
 <Form.Control type="date" size="sm" value={form.forecastEndDate}
 onChange={(e) => setF("forecastEndDate", e.target.value)} />
 <Form.Text className="text-muted" style={{ fontSize: 10 }}>Contractor's current forecast</Form.Text>
 </Col>
 <Col md={3}>
 <Form.Label className="small fw-semibold">Actual Start</Form.Label>
 <Form.Control type="date" size="sm" value={form.actualStartDate}
 onChange={(e) => setF("actualStartDate", e.target.value)} />
 </Col>
 <Col md={3}>
 <Form.Label className="small fw-semibold">Actual End</Form.Label>
 <Form.Control type="date" size="sm" value={form.actualEndDate}
 onChange={(e) => setF("actualEndDate", e.target.value)} />
 </Col>
 <Col md={3}>
 <Form.Label className="small fw-semibold">Status</Form.Label>
 <Form.Select size="sm" value={form.status} onChange={(e) => setF("status", e.target.value)}>
 {STATUSES.map((s) => <option key={s}>{s}</option>)}
 </Form.Select>
 </Col>
 <Col md={3}>
 <Form.Label className="small fw-semibold">Completion % — {form.completionPercentage}%</Form.Label>
 <Form.Range min={0} max={100} step={5} value={form.completionPercentage}
 onChange={(e) => setF("completionPercentage", e.target.value)} />
 </Col>

 {/* Delay fields */}
 <Col md={4}>
 <Form.Label className="small fw-semibold">
 <i className="fas fa-user-tag me-1 text-muted" />Delay Responsibility Code
 </Form.Label>
 <Form.Select size="sm" value={form.delayResponsibility}
 onChange={(e) => setF("delayResponsibility", e.target.value)}>
 {DELAY_RESP_CODES.map((d) => (
 <option key={d.value} value={d.value}>{d.label}</option>
 ))}
 </Form.Select>
 </Col>
 <Col md={4} className="d-flex flex-column justify-content-end">
 <Form.Check
 type="switch"
 id="fm-switch"
 label={<span className="small fw-semibold"><Badge bg="info" className="me-1">FM</Badge>Force Majeure Event</span>}
 checked={form.isForceMajeure}
 onChange={(e) => setF("isForceMajeure", e.target.checked)}
 />
 <Form.Text className="text-muted" style={{ fontSize: 10 }}>
 Excluded from contractor-attributable delay
 </Form.Text>
 </Col>
 <Col md={4} className="d-flex flex-column justify-content-end">
 <Form.Check
 type="switch"
 id="cd-switch"
 label={<span className="small fw-semibold"><Badge bg="secondary" className="me-1">CD</Badge>Concurrent Delay</span>}
 checked={form.hasConcurrentDelay}
 onChange={(e) => setF("hasConcurrentDelay", e.target.checked)}
 />
 <Form.Text className="text-muted" style={{ fontSize: 10 }}>
 Root cause documented in Remarks — PMC to adjudicate
 </Form.Text>
 </Col>

 <Col md={12}>
 <Form.Label className="small fw-semibold">Remarks</Form.Label>
 <Form.Control as="textarea" rows={2} size="sm" value={form.remarks}
 onChange={(e) => setF("remarks", e.target.value)}
 placeholder="Root cause, recovery plan, or any notes" />
 </Col>
 </Row>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="outline-secondary" onClick={() => setShowModal(false)}>Cancel</Button>
 <Button variant="primary" onClick={handleSave} disabled={saving}>
 {saving
 ? <><Spinner size="sm" className="me-2" />Saving…</>
 : <><i className="fas fa-save me-1" />{editingId ? "Update" : "Create"}</>}
 </Button>
 </Modal.Footer>
 </Modal>
 </>
 );
};

export default MilestoneTracker;
